/**
 * PlaylistShareButton — Icon button that opens the PlaylistSharePreview modal.
 *
 * Renders a borderless share icon (SHARE_ANDROID) sized to match FollowTag.
 * When clicked, it opens the share preview modal for the given playlist.
 *
 * i18n: owns its own `playlistShareButton` slice registered in
 * `services/static-text/language-*.js`. Callers do NOT thread `staticText`
 * down — the HOC injects it and the Outer/Inner pattern provides the
 * surrounding context for the inner hook to resolve `sharePlaylist`.
 */

import React, {
  useState, useCallback, useMemo, Suspense,
} from 'react'
import { createPortal } from 'react-dom'
import { compose } from 'recompose'
import Button, { TYPES as BUTTON_TYPES } from '../../../Button/index'
import ShareAndroid from '../../../Icon/icons/ShareAndroid/index'
import { usePostHogEvent } from '../../../PostHog/index'
import { connect as connectStaticText } from '../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../StaticText/StaticTextContext'
import type { EnrichedPlaylistItem } from '../../../../services/community/types'
import usePreloadImages from './usePreloadImages'

const SHARE_PREVIEW_LOADER = () => import('../../Playlist/SharedPage/PlaylistSharePreview/index')
const PlaylistSharePreview = React.lazy(SHARE_PREVIEW_LOADER)

const MAX_PRELOAD = 3

export interface PlaylistShareButtonProps {
  playlistId: string
  playlistName: string
  description?: string
  items: EnrichedPlaylistItem[]
}

interface PlaylistShareButtonOuterProps extends PlaylistShareButtonProps {
  /** Static-text Map injected by `connectStaticText({ storeKey: 'playlistShareButton' })`. */
  staticText: InternationalStrings
}

function PlaylistShareButtonContent ({
  playlistId,
  playlistName,
  description,
  items,
}: PlaylistShareButtonProps) {
  const [showPreview, setShowPreview] = useState(false)

  const ariaLabel = useTranslatedText({ keys: ['sharePlaylist'] })

  // Intent signal — fires the moment the user clicks the share icon (modal
  // open), distinct from `community_playlist_shared` which only fires after
  // the user clicks Copy/Native inside the modal. Pair them in a funnel to
  // compute share-modal abandonment rate.
  const trackShareInitiated = usePostHogEvent('community_playlist_share_initiated')

  const handleOpen = useCallback(() => {
    if (playlistName) trackShareInitiated(playlistName)
    setShowPreview(true)
  }, [playlistName, trackShareInitiated])

  const handleDismiss = useCallback(() => {
    setShowPreview(false)
  }, [])

  const preloadUrls = useMemo(
    () => (items || [])
      .map((item) => item.imageUrl)
      .filter(Boolean)
      .slice(0, MAX_PRELOAD),
    [items],
  )
  const { preload } = usePreloadImages(preloadUrls, { eager: false })

  if (!items || items.length === 0) {
    return null
  }

  return (
    <>
      <span
        className="playlist-share-button-wrap"
        onFocusCapture={() => {
          SHARE_PREVIEW_LOADER().catch(() => {})
          preload()
        }}
      >
        <Button
          type={BUTTON_TYPES.LINK}
          className="playlist-share-button"
          onClick={handleOpen}
          onMouseEnter={() => {
            SHARE_PREVIEW_LOADER().catch(() => {})
            preload()
          }}
          aria-label={ariaLabel}
        >
          <ShareAndroid />
        </Button>
      </span>
      {showPreview ? createPortal(
        <Suspense fallback={null}>
          <PlaylistSharePreview
            visible
            playlistId={playlistId}
            playlistName={playlistName}
            description={description}
            items={items}
            onDismiss={handleDismiss}
          />
        </Suspense>,
        document.body,
      ) : null}
    </>
  )
}

// Outer wrapper: receives `staticText` from connectStaticText and provides
// it to the subtree so Content's useTranslatedText resolves against the
// `playlistShareButton` slice. Mirrors the Oracle Outer/Inner pattern.
function PlaylistShareButton ({
  staticText,
  ...rest
}: PlaylistShareButtonOuterProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <PlaylistShareButtonContent {...rest} />
    </StaticTextProvider>
  )
}

export default compose(
  connectStaticText({ storeKey: 'playlistShareButton' }),
)(React.memo(PlaylistShareButton))
